import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./pluginSpecs.html";
import { PackageMembersFetcher } from '~/modules/helpers/packageMembersFetchHelper';
import { getCurrentLocalePrefix } from '~/modules/helpers/localeHelper';
import { WireI18n } from '~/modules/stores/i18nStore';
import { getPluginSkillIds, getPluginSkillSummaries, shouldDisablePluginSkillLinks } from '~/modules/utilities/plugin_utils';
import { generateListingUrl } from '~/modules/utilities/listing_utils';

/**
 * Plugin Specs tab body: resolves the plugin's member skills and renders them through the shared
 * `listing-package-contents-viewer` as one "Skills" section. Degrades to the viewer's own empty
 * state if the BFF returns [] (soft-failed resolution) — never throws.
 */
class PluginSpecs extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    // Reactive fields must be initialized (not merely declared) to re-render under the client build.
    this.members = [];
    this.isLoadingMembers = false;
    this.disableSkillLinks = false;
    this.usesSkillSummaries = false;
    this.membersFetcher = new PackageMembersFetcher(this, getPluginSkillIds);
  }
  get listing() {
    return this._listing;
  }

  // Setter is the single fetch trigger: also covers in-place navigation, where a keyless
  // if:true in the parent reassigns this property instead of remounting the component.
  set listing(value) {
    this._listing = value;
    this.disableSkillLinks = shouldDisablePluginSkillLinks(value);
    const summaries = getPluginSkillSummaries(value);
    this.usesSkillSummaries = this.disableSkillLinks && summaries.length > 0;
    if (this.usesSkillSummaries) {
      const publisher = value.publisher?.name ?? '';
      this.membersFetcher.useProvidedMembers(value, summaries.map(summary => ({
        id: summary.externalId,
        name: summary.name,
        description: summary.description,
        publisher,
        logoUrl: summary.logoUrl ?? '',
        technology: 'ai-skill'
      })));
      return;
    }
    this.membersFetcher.onListingChange(value);
  }
  disconnectedCallback() {
    this.membersFetcher.disconnect();
  }
  get viewerTitle() {
    return this.labels?.viewerTitle ?? '';
  }
  get searchPlaceholder() {
    return this.labels?.searchPlaceholder ?? '';
  }
  get loadingLabel() {
    return this.labels?.loading ?? '';
  }
  get emptySearchLabel() {
    return this.labels?.emptySearch ?? '';
  }
  get displayListLabel() {
    return this.labels?.displayListLabel ?? '';
  }
  get displayCardsLabel() {
    return this.labels?.displayCardsLabel ?? '';
  }
  get skillsSectionHeading() {
    return this.labels?.skillsSectionHeading ?? '';
  }
  get skillTypeLabel() {
    return this.labels?.skillTypeLabel ?? '';
  }
  get rollupTiles() {
    return [{
      label: this.skillsSectionHeading,
      count: this.members.length
    }];
  }
  get sections() {
    if (this.members.length === 0) {
      return [];
    }
    return [{
      heading: this.skillsSectionHeading,
      items: this.members.map(member => {
        // Purely relative, never host-qualified: members are same-origin in whichever app
        // the user is browsing, so the link keeps them in the current experience on every domain.
        const url = !this.disableSkillLinks ? generateListingUrl('', {
          technology: member.technology
        }, `listingId=${encodeURIComponent(member.id)}`, getCurrentLocalePrefix()) : '';
        return {
          id: member.id,
          title: member.name,
          subtitle: member.publisher,
          description: member.description,
          logoUrl: member.logoUrl,
          typeLabel: this.skillTypeLabel,
          // Skill badge icon, matching wds-listing-card's SKILL_BADGE_ICON.
          typeIcon: 'work_order_type',
          url,
          isNavigable: !this.disableSkillLinks,
          categories: [],
          // Card view renders this member through the same wds-listing-card the Skills
          // landing page uses; the blue "Skill" badge is auto-detected from type: 'ai-skill'.
          // No reviews are fetched for plugin members, so reviewsAmount: 0 hides the
          // rating row, and there's no per-skill pricing to show.
          cardData: {
            oafId: member.id,
            type: 'ai-skill',
            listingUrl: url,
            title: member.name,
            publisher: member.publisher,
            description: member.description,
            image: member.logoUrl,
            resolvedImageUrl: this.usesSkillSummaries ? member.logoUrl : undefined,
            pricing: '',
            reviewsAmount: 0,
            averageRating: 0,
            listingCategories: [],
            chatEnabled: false,
            sponsored: false
          }
        };
      })
    }];
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(PluginSpecs, {
  publicProps: {
    listing: {
      config: 3
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills.packageContents
        };
      }
    }
  },
  fields: ["members", "isLoadingMembers", "disableSkillLinks", "usesSkillSummaries", "membersFetcher"]
});
const __lwc_component_class_internal = _registerComponent(PluginSpecs, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;